import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import type { EventDescription } from '~/composables/types/event'
import type { TypesenseClientHandle } from '~/composables/search/useSearchClient'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { useTypesenseHandle } from '~/composables/search/useTypesenseHandle'
import { fetchTypesenseSourceEvent } from '~/composables/search/useTypesenseSourceEvent'
import { buildSimilarOnlineEventsUrl } from '~/composables/product'
import { kftApiRequest } from '~/utils/kftApiRequest'
import { transformToAlgoliaEvent } from '~/utils/helpers'
import { typesenseSearch } from '~/utils/typesenseClient'
import { buildVectorSimilarOnlineEventsQuery } from '~/utils/typesenseSimilarEventsQuery'
import { getEventListIndexName } from '~/utils/searchEngineHelpers'
import { toSearchEngineEventItem } from '~/utils/adaptTypesenseEventDocument'

/** Vector nearest-neighbour similar online events from Typesense (KFT-4032). */
const fetchSimilarOnlineEventsTypesense = async (
  permalink: string,
  collection: string,
  handle: TypesenseClientHandle
): Promise<SearchEngineEventItem[]> => {
  const source = await fetchTypesenseSourceEvent(permalink, collection, handle)
  if (!source) return []

  const params = buildVectorSimilarOnlineEventsQuery(source, collection)
  // Static per event, so every viewer of this event sends the same request.
  const result = await typesenseSearch<Record<string, unknown>>(params, {
    ...handle,
    useCache: true,
  })

  if (import.meta.dev) {
    console.debug(
      `[KFT-4032] similar online events (vector): ${result.search_time_ms}ms server, ${result.hits?.length ?? 0} hits`
    )
  }

  return (result.hits ?? []).map(({ document }) =>
    toSearchEngineEventItem(document)
  )
}

/**
 * Similar online events for the event page, per the shared search engine
 * (KFT-4115): `algolia` → KFT API online events in the main category,
 * `typesense` → Typesense vector query from the event itself. The engine is in
 * the key, so an A/B force after hydration refetches from the other backend.
 */
export const useSimilarOnlineEvents = (
  permalink: string,
  mainCategorySlug: string | undefined
) => {
  const { locale } = useI18n()
  const engine = useSearchEngine({ clientOnly: true })
  const typesenseHandle = useTypesenseHandle()

  return useAsyncData<SearchEngineEventItem[]>(
    () => `similar-online-events-${engine.value}-${permalink}`,
    async () => {
      if (engine.value === 'typesense') {
        if (!permalink) return []
        return fetchSimilarOnlineEventsTypesense(
          permalink,
          getEventListIndexName(locale.value),
          typesenseHandle
        )
      }

      if (!mainCategorySlug) return []
      const response = await kftApiRequest<EventDescription[]>(
        buildSimilarOnlineEventsUrl(mainCategorySlug),
        { method: 'GET' }
      )
      // Same partial shape the KFT API path always produced (useFetch transform).
      return transformToAlgoliaEvent(response) as SearchEngineEventItem[]
    },
    { server: false, default: () => [], dedupe: 'defer' }
  )
}
